// The view-state store: holds the current ViewState, applies changes as new
// states (never in place) and tells subscribers. url-sync.ts mirrors it into the
// address; features read it and change it through FeatureContext.state.

import { overlay, viewBase, findView, NAMED_VIEWS, type NamedView } from "./views";
import { DEFAULT_VIEW_STATE, cloneState, jsonEqual, type PartialViewState, type ViewState } from "./view-state";

export type StateListener = (state: ViewState, previous: ViewState) => void;

export class ViewStore {
  private current: ViewState;
  private readonly listeners = new Set<StateListener>();
  readonly views: readonly NamedView[];

  constructor(initial: ViewState = DEFAULT_VIEW_STATE, views: readonly NamedView[] = NAMED_VIEWS) {
    this.current = cloneState(initial);
    this.views = views;
  }

  /** The current state. Treat it as read-only; change it with set() or update(). */
  get state(): ViewState {
    return this.current;
  }

  /** Lays a partial state over the current one. Invalid values are ignored and returned as issues. */
  set(patch: PartialViewState): string[] {
    const { state, issues } = overlay(this.current, patch, "patch");
    this.commit(state);
    return issues;
  }

  /** Changes a copy of the current state and commits it. */
  update(change: (draft: ViewState) => void): void {
    const draft = cloneState(this.current);
    change(draft);
    // Through overlay, so a bad value from a feature cannot get into the state.
    const { state, issues } = overlay(this.current, draft, "update");
    for (const issue of issues) console.warn(`view state: ${issue}`);
    this.commit(state);
  }

  /** Replaces the whole state (after reading an address, say). */
  replace(state: ViewState): void {
    this.commit(cloneState(state));
  }

  /** Switches to a named view: the defaults with the view laid over them. */
  applyView(id: string): string[] {
    const view = findView(id, this.views);
    if (view === undefined) return [`no such named view: ${id}`];
    const { state, issues } = viewBase(view);
    this.commit(state);
    return issues;
  }

  /** Calls `listener` on every change. Returns the unsubscribe function. */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private commit(next: ViewState): void {
    if (jsonEqual(next, this.current)) return;
    const previous = this.current;
    // Unchanged sections keep their identity, so a subscriber can test a section with !==.
    const shared = next as unknown as Record<string, unknown>;
    for (const [key, value] of Object.entries(previous)) {
      if (jsonEqual(shared[key], value)) shared[key] = value;
    }
    this.current = next;
    for (const l of [...this.listeners]) l(next, previous);
  }
}
